import {
  Braces,
  Boxes,
  Zap,
  Layers,
  Plug,
  ShieldCheck,
  Gauge,
  Server,
} from "lucide-react";
import {
  PageHero,
  Container,
  SectionHead,
  FeatureGrid,
  CTASection,
  CheckList,
} from "@/components/common/Section";
import { Reveal } from "@/lib/anim";
import ArchitectureDiagram from "@/components/visuals/ArchitectureDiagram";

const PRINCIPLES = [
  {
    icon: <Braces className="h-5 w-5" />,
    title: "API-first",
    desc: "Every capability is exposed as a well-defined API. Platforms integrate, extend and embed by default.",
  },
  {
    icon: <Boxes className="h-5 w-5" />,
    title: "Microservices",
    desc: "Independently deployable services for payments, wallets, ledger, accounts and transactions.",
  },
  {
    icon: <Zap className="h-5 w-5" />,
    title: "Event-driven processing",
    desc: "Asynchronous, event-driven flows keep transactions resilient and systems decoupled.",
  },
  {
    icon: <Layers className="h-5 w-5" />,
    title: "Multi-tenancy",
    desc: "Tenant isolation, configuration and branding designed into the core, not bolted on.",
  },
  {
    icon: <Plug className="h-5 w-5" />,
    title: "Integration architecture",
    desc: "An extensible connector layer with 60+ pre-built integrations for banks, PSPs, KYC, fraud and FX.",
  },
  {
    icon: <ShieldCheck className="h-5 w-5" />,
    title: "Security",
    desc: "Encryption, access control, audit logging and monitoring engineered into every layer.",
  },
  {
    icon: <Gauge className="h-5 w-5" />,
    title: "Scalability",
    desc: "Horizontally scalable services designed for growing transaction volumes and tenant counts.",
  },
  {
    icon: <Server className="h-5 w-5" />,
    title: "Deployment options",
    desc: "Cloud, hybrid or customer-managed deployments aligned to your operating and regulatory model.",
  },
];

const SECURITY_POINTS = [
  "Encryption of sensitive payment data",
  "Role-based access control",
  "Controlled API gateway connectivity",
  "Comprehensive audit logging",
  "Platform and transaction monitoring",
  "Built with financial industry requirements in mind",
];

export default function Technology() {
  return (
    <>
      <PageHero
        eyebrow="Technology"
        title="Engineered for scale. Designed for change."
        copy="The reference architecture behind iJeeva platforms: API-first, event-driven and multi-tenant, with an extensible integration layer at its core."
        testid="technology-page-title"
      />

      <section className="py-20 lg:py-24">
        <Container>
          <SectionHead
            center
            eyebrow="Architecture"
            title="A clean separation between channels, services and rails."
          />
          <div className="mt-14">
            <ArchitectureDiagram showAttributes={false} />
          </div>
        </Container>
      </section>

      <section className="border-y border-[#E2E8F0] bg-white py-20 lg:py-24">
        <Container>
          <SectionHead
            eyebrow="Engineering Principles"
            title="How iJeeva platforms are built."
            copy="Consistent architectural principles across every platform and engagement."
          />
          <div className="mt-14">
            <FeatureGrid items={PRINCIPLES} cols={4} testidPrefix="technology-principle" />
          </div>
        </Container>
      </section>

      <section className="bg-[#1D305B] py-20 lg:py-24">
        <Container className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <span className="inline-flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#5EEAD4]">
              <span className="h-px w-6 bg-[#5EEAD4]/60" aria-hidden="true" />
              Security by Design
            </span>
            <h2 className="mt-4 text-[30px] font-semibold leading-[1.15] tracking-tight text-white sm:text-4xl">
              Security is engineered into the platform.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#94A3B8] sm:text-lg">
              Payment infrastructure demands more than functionality. It requires security, control and operational
              resilience at every layer.
            </p>
          </Reveal>
          <Reveal delay={140}>
            <CheckList dark twoCol items={SECURITY_POINTS} />
          </Reveal>
        </Container>
      </section>

      <CTASection
        title="Want to go deeper on architecture?"
        copy="Our engineering team can walk through the platform design against your requirements."
      />
    </>
  );
}
